import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import { ACTIONS, URLS, APIS, STORAGE } from '../_config';
import { Link, useNavigate, Outlet, Navigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useStoreSelector, useAuthStatus, useStoreBrnDSSelector, useBranchRefreshTimestampSelector } from '../store/selectors/userSelector';
import { useStoreApi } from '../store/api/hooks/storeApiHook';
import { useDataSourceApi } from '../store/api/hooks/dataSourceApiHook';
import { SwalService } from './confirmation-popup/swalService';
import Spinner from './iconSvgs/spinner';
import { AnyListenerPredicate } from '@reduxjs/toolkit';

const Header: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const store = useStoreSelector();
    const branchDS = useStoreBrnDSSelector();
    const branchRefreshTimestamp = useBranchRefreshTimestampSelector();
    const storeAPI = useStoreApi();
    const dataSourceAPI = useDataSourceApi();
    // console.log('store =>', branchDS, branchDS?.branch_name);
    const [isOnSubmitLoading, setOnSubmitLoading] = useState<boolean>(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [selectedBranch, setSelectedBranch] = useState<any>(branchDS);


    useEffect(() => {
        const data: any = {
            store_id: store?.store_id
        };
        getBranches(data);
    }, []);

    // Re-fetch branches when branchRefreshTimestamp changes (after a new branch is created)
    useEffect(() => {
        if (branchRefreshTimestamp) {
            const data: any = {
                store_id: store?.store_id
            };
            getBranches(data);
        }
    }, [branchRefreshTimestamp]);

    const getBranches = (data: any) => {
        storeAPI.getStoreBranches(data, (msg: any, resp: any) => {
            // console.log('response =>', resp, msg);
            setBranches(resp.data);
        }, (err: any) => {
            toast.error(err);
        })
    }


    const handleSubmit = (branch: any) => {
        onSubmit(branch);
    };

    const handleClick = (branch: any) => {
        SwalService.triggerSwal(
            'Default Branch',
            'Are you sure you want to change store branch?',
            () => handleSubmit(branch)
        );
    };

    const onSubmit = (branch: any) => {
        // console.log('selectedBranch =>', selectedBranch);
        setOnSubmitLoading(true);
        const param: any = {
            branch_id: branch?.branch_id,
        }
        try {
            setTimeout(() => {
                dataSourceAPI.getDataSource(param, (msg: any, resp: any) => {
                    if (resp?.code === 200) {
                        if (resp.data.length > 0) {
                            let inPending: any = null;
                            let failure: any = null;
                            const completed = resp?.data.find((item: any) => item.last_fetch_status === 'completed');
                            if (!completed) {
                                inPending = resp?.data.find((item: any) => item.last_fetch_status === 'pending');
                                if (!inPending) {
                                    failure = resp?.data.find((item: any) => item.last_fetch_status !== 'completed' && item.last_fetch_status !== 'pending');
                                }
                            }
                            if (completed) {
                                dispatch({
                                    type: ACTIONS.STORE.GET_BRANCH,
                                    payload: completed,
                                })
                                navigate(URLS.DASHBOARD);
                                window.location.reload();
                            } else if (inPending) {
                                dispatch({
                                    type: ACTIONS.STORE.GET_BRANCH,
                                    payload: inPending,
                                })
                                navigate(URLS.DATASOURCES.LIST);
                                window.location.reload();
                            } else {
                                dispatch({
                                    type: ACTIONS.STORE.GET_BRANCH,
                                    payload: failure,
                                })
                                navigate(URLS.DATASOURCES.LIST);
                                window.location.reload();
                            }
                        }
                        // window.location.reload();
                    } else if (resp?.code === 404) {
                        // toast.error(resp?.message);
                        const payload = {
                            source_type: null,
                            url: null,
                            last_fetch_date: null,
                            created_at: null,
                            ds_id: null,
                            store_id: store?.store_id,
                            branch_id: branch?.branch_id,
                            branch_name: branch?.branch_name,
                            snapshot_id: null,
                            last_fetch_status: null
                        }
                        dispatch({
                            type: ACTIONS.STORE.GET_BRANCH,
                            payload: payload,
                        })
                        navigate(URLS.DATASOURCES.CREATE);
                        window.location.reload();
                    }
                }, (error: any, resp: any) => {
                    console.log('error =>', error, resp);
                    if (resp?.detail?.code === 404) {
                        // toast.error(resp?.detail?.message);
                        const payload = {
                            source_type: null,
                            url: null,
                            last_fetch_date: null,
                            created_at: null,
                            ds_id: null,
                            store_id: store?.store_id,
                            branch_id: branch?.branch_id,
                            branch_name: branch?.branch_name,
                            snapshot_id: null,
                            last_fetch_status: null
                        }
                        dispatch({
                            type: ACTIONS.STORE.GET_BRANCH,
                            payload: JSON.stringify(payload),
                        })
                        navigate(URLS.DATASOURCES.CREATE);
                        window.location.reload();
                    } else {
                        // toast.error("Something went wrong");
                    }
                })
                setOnSubmitLoading(false);
            }, 3000);
        } catch (err: any) {
            toast.error(err);
            setOnSubmitLoading(false);
        }
    };

    return (
        <>
            {isOnSubmitLoading && (
                <div className="absolute inset-0 bg-gray-700 bg-opacity-50 flex justify-center items-center z-50">
                    <Spinner className="self-center z-50" />
                </div>
            )}
            <div className="flex w-full items-center justify-between gap-2 bg-white p-3 rounded-r-md -mb-5">
                <div className='ipad-padding-left min-w-0 flex-1'>
                    <h4 className='font-semibold text-gray-500 truncate'>{store?.name}</h4>
                </div>
                <div className="shrink-0 w-[150px] sm:w-[200px] lg:w-[220px]">
                    <Select
                        options={branches.map((brn: any) => ({ value: brn.branch_id, label: brn.branch_name }))}
                        value={selectedBranch ? { value: selectedBranch.branch_id, label: selectedBranch.branch_name } : null}
                        onChange={(option: any) => {
                            const branch = branches.find((b: any) => b.branch_id === option?.value);
                            if (branch) handleClick(branch);
                        }}
                        placeholder="Branch"
                        isSearchable={false}
                        styles={{
                            control: (base, state) => ({
                                ...base,
                                borderRadius: '4px',
                                borderColor: state.isFocused ? '#3b82f6' : '#e5e7eb',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#3b82f6' },
                                minHeight: '34px',
                                fontSize: '14px',
                                backgroundColor: '#f9fafb',
                                cursor: 'pointer',
                            }),
                            option: (base, state) => ({
                                ...base,
                                color: state.isSelected ? 'white' : '#374151',
                                backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
                                fontSize: '14px',
                            }),
                            singleValue: (base) => ({ ...base, color: '#374151', fontSize: '14px' }),
                            dropdownIndicator: (base) => ({ ...base, color: '#374151', padding: '0 6px' }),
                            indicatorSeparator: () => ({ display: 'none' }),
                        }}
                    />
                </div>
            </div>
        </>
    )
}
export default Header;